import { useCallback, useEffect, useMemo, useState } from "react";

import {
  DEFAULT_PALETTE,
  loadDashboardConfig,
  paletteOf,
  saveDashboardConfig,
} from "../../../orion/config/dashboards";

/**
 * What colour the cards are.
 * ---------------------------------------------------------------------------
 * The dashboards already have this: a set of shared palettes, a colour of your
 * own spread into ten swatches, and one action to put the choice on every
 * board. The card pages take the same palettes rather than inventing a second
 * set - a card's tint is an index from 0 to 9 drawn from its own id (see
 * cardSeed), and a palette is exactly ten swatches, so the two line up without
 * anything in between.
 *
 * Where the choice lives says how far it reaches:
 *
 *   cards.palette.<page>   this page only - applications, say, left on Ember
 *                          while the estate stays on its own tints
 *   cards.palette.all      every card page, for someone who wants one colour
 *                          across the product
 *
 * A page reads its own key first and the shared one after it, so "set it here"
 * always beats "set it everywhere", and clearing the page's own key is what
 * puts it back under the shared choice. `applyEverywhere` writes the shared
 * key, drops the per-page ones, and puts the same palette on every dashboard -
 * which is the whole of "globally", including the boards.
 * ---------------------------------------------------------------------------
 */

/* The cards' own ten tints, defined per theme in ForgeWiki.css. Not a palette
   in the dashboards' sense - it is the product's own surface ladder, lifted
   from the page's ground - so it is named rather than built. */
export const ESTATE_PALETTE = "estate";

/* One event for every page open at once, so a palette set on the applications
   page while discovery is mounted behind it does not leave the two
   disagreeing. */
const CHANGED = "cards-palette-changed";

const PAGE_KEY = (scope) => `cards.palette.${scope}`;
const ALL_KEY = "cards.palette.all";

/* Palettes someone made, wherever they made them.
   -------------------------------------------------------------------------
   A custom palette used to live inside whichever thing it was made on - a
   dashboard kept it in its own config, a card page in its own key - so one
   built on a board could not be chosen on the cards, and the other way round.
   They are kept here instead, in one list the pickers all read, and choosing
   one anywhere is choosing the same object: a colour, a scheme and a name. */
const CUSTOM_KEY = "palette.custom.list";

/** A stable identity for a custom palette: what defines it.
    The pair, for one built from a colour and a scheme - and the ten themselves
    for one that carries its own, where the pair says nothing about it. */
export const customId = (p) => (Array.isArray(p?.colors) && p.colors.length
  ? `custom:${p.colors.join(",").toLowerCase()}`
  : `custom:${String(p?.base || "").toLowerCase()}:${p?.scheme || "analogous"}`);

export function loadCustomPalettes() {
  try {
    const raw = JSON.parse(localStorage.getItem(CUSTOM_KEY) || "[]");
    return Array.isArray(raw) ? raw.filter(p => p && p.base) : [];
  } catch (e) { return []; }
}

/**
 * Keep one. The pair is the identity, so editing the colour of a named palette
 * saves a new one and renaming an existing pair renames it in place - which is
 * what someone fiddling with the two controls expects to happen.
 */
export function saveCustomPalette(palette) {
  if (!palette || !palette.base) return loadCustomPalettes();
  const list = loadCustomPalettes();
  const id = customId(palette);
  const at = list.findIndex(p => customId(p) === id);
  const entry = {
    base: palette.base,
    scheme: palette.scheme || "analogous",
    name: (palette.name || "").trim(),
    ...(Array.isArray(palette.colors) && palette.colors.length ? { colors: palette.colors } : {}),
  };
  if (at >= 0) list[at] = { ...list[at], ...entry };
  else list.unshift(entry);
  try { localStorage.setItem(CUSTOM_KEY, JSON.stringify(list.slice(0, 24))); } catch (e) { /* private window */ }
  window.dispatchEvent(new Event(CHANGED));
  return list;
}

export function forgetCustomPalette(palette) {
  const id = customId(palette);
  const list = loadCustomPalettes().filter(p => customId(p) !== id);
  try { localStorage.setItem(CUSTOM_KEY, JSON.stringify(list)); } catch (e) { /* private window */ }
  window.dispatchEvent(new Event(CHANGED));
  return list;
}

/** The saved palettes, kept in step with whatever else is open. */
export function useCustomPalettes() {
  const [list, setList] = useState(loadCustomPalettes);
  useEffect(() => {
    const sync = () => setList(loadCustomPalettes());
    window.addEventListener(CHANGED, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGED, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return list;
}

/* The choice travels as JSON because it is either an id or a whole custom
   palette, and the two have to come back as what they went in as. */
function read(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    if (raw.startsWith("{")) return JSON.parse(raw);
    return raw;
  } catch (e) { return null; }
}

function write(key, value) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, typeof value === "string" ? value : JSON.stringify(value));
  } catch (e) { /* private window */ }
}

/** The ten colours a choice resolves to, or null for the cards' own tints. */
export function paletteColors(value) {
  if (!value || value === ESTATE_PALETTE) return null;
  const colors = paletteOf(value)?.nodePalette;
  return Array.isArray(colors) && colors.length ? colors : null;
}

/**
 * Set the palette for one page, or for all of them, without being a component.
 * ---------------------------------------------------------------------------
 * The hook below does this through React state, which is what a picker wants.
 * A tool is not a picker: it is called from outside the tree, by the butler or
 * by an agent in the browser, and it needs the same two writes without a
 * component to hang them on. So the writes live here and the hook calls them,
 * which also means there is one definition of what "everywhere" means.
 * ---------------------------------------------------------------------------
 */
export function setPaletteFor(scope, next) {
  if (next && typeof next === "object" && next.base) saveCustomPalette(next);
  const shared = read(ALL_KEY);
  const sameAsShared = shared != null && JSON.stringify(shared) === JSON.stringify(next);
  write(PAGE_KEY(scope), sameAsShared ? null : next);
  window.dispatchEvent(new Event(CHANGED));
}

/** ...and on every card page and every dashboard. */
export async function setPaletteEverywhere(next) {
  if (next && typeof next === "object" && next.base) saveCustomPalette(next);
  write(ALL_KEY, next);
  ["wiki", "discovery", "apps"].forEach(s => write(PAGE_KEY(s), null));
  window.dispatchEvent(new Event(CHANGED));

  /* The estate's own tints have no counterpart on a board, so the boards are
     left as they are rather than being pushed onto a palette that is not one
     of theirs. */
  if (!next || next === ESTATE_PALETTE) return 0;

  try {
    const config = await loadDashboardConfig();
    const dashboards = (config?.dashboards || []).map(d => ({ ...d, palette: next }));
    if (!dashboards.length) return 0;
    await saveDashboardConfig({ ...config, dashboards });
    return dashboards.length;
  } catch (e) {
    return 0;
  }
}

/** What every card page is currently painted from. */
export const sharedPalette = () => read(ALL_KEY) ?? ESTATE_PALETTE;

/**
 * The palette for one card page.
 *
 * @param scope  which page is asking - "wiki", "discovery", "apps"
 */
export function useCardPalette(scope) {
  const [page, setPage] = useState(() => read(PAGE_KEY(scope)));
  const [all, setAll] = useState(() => read(ALL_KEY));

  useEffect(() => {
    const sync = () => { setPage(read(PAGE_KEY(scope))); setAll(read(ALL_KEY)); };
    window.addEventListener(CHANGED, sync);
    /* and from another tab, where `storage` is the only word of it */
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGED, sync);
      window.removeEventListener("storage", sync);
    };
  }, [scope]);

  /* This page's own choice first, the shared one behind it. */
  const value = page ?? all ?? ESTATE_PALETTE;
  const colors = useMemo(() => paletteColors(value), [value]);

  /* Set it here. Choosing the same thing the shared key already says drops
     this page's override rather than pinning it, so a later "everywhere"
     still moves this page too. */
  const setValue = useCallback((next) => setPaletteFor(scope, next), [scope]);

  /* Set it everywhere: the shared key, every other card page's override
     cleared, and the same palette on every dashboard. */
  const applyEverywhere = useCallback(async (next) => setPaletteEverywhere(next), []);

  return { value, colors, setValue, applyEverywhere, shared: all ?? ESTATE_PALETTE, DEFAULT_PALETTE };
}

export default useCardPalette;
